import { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { useIntroGenerator } from '@/hooks/ai';
import { cn } from '@/lib/utils';

export function AIIntroGenerator() {
  const { introduction, isLoading, error, generate, reset } = useIntroGenerator();
  const [formData, setFormData] = useState({
    name: '',
    profession: '',
    strengths: '',
    hobbies: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await generate({
      name: formData.name,
      profession: formData.profession,
      strengths: formData.strengths.split(',').map(s => s.trim()).filter(Boolean),
      hobbies: formData.hobbies.split(',').map(s => s.trim()).filter(Boolean),
    });
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI 自己紹介文ジェネレーター</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium">名前</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="山田 太郎"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">職種</label>
            <input
              type="text"
              value={formData.profession}
              onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Webエンジニア"
              required
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">強み（カンマ区切り）</label>
          <input
            type="text"
            value={formData.strengths}
            onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="問題解決力, コミュニケーション力, 技術への探究心"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">趣味（カンマ区切り）</label>
          <input
            type="text"
            value={formData.hobbies}
            onChange={(e) => setFormData({ ...formData, hobbies: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="読書, プログラミング, 旅行"
          />
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              "inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground",
              isLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-primary/90"
            )}
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                生成する
              </>
            )}
          </button>
          {introduction && (
            <button
              type="button"
              onClick={reset}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              リセット
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {introduction && (
        <div className="mt-4 rounded-md bg-secondary p-4">
          <h4 className="mb-2 text-sm font-medium">生成された自己紹介文:</h4>
          <p className="text-sm text-muted-foreground">{introduction}</p>
        </div>
      )}
    </div>
  );
}
